/**
 * Generic utilities for reading and removing URL parameters.
 */

type UrlParamValue = string | null;

type ParamsFromNames<TNames extends readonly string[]> = {
  [K in TNames[number]]: UrlParamValue;
};

/**
 * Read specific URL parameters and remove them from the URL string.
 * Returns the parameter values and the new URL string.
 *
 * @param url - The full URL string to process (or relative path)
 * @param paramNames - Array of parameter names to read and remove (use `as const` to get typed keys)
 * @returns Object with parameter values and the new URL string
 */
export function readAndRemoveUrlParams<const TNames extends readonly string[]>(
  url: string,
  paramNames: TNames,
): {
  params: ParamsFromNames<TNames>;
  newUrl: string;
} {
  const params = {} as ParamsFromNames<TNames>;

  // Check if URL is absolute
  const isAbsolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url);

  const placeholderBase = "http://placeholder";
  const urlInstance = new URL(url, isAbsolute ? undefined : placeholderBase);

  for (const name of paramNames) {
    const key = name as TNames[number];
    params[key] = urlInstance.searchParams.get(key);
    urlInstance.searchParams.delete(key);
  }

  let newUrl = urlInstance.toString();

  if (!isAbsolute) {
    // Remove the placeholder base
    // We use slice to ensure we only remove the start
    if (newUrl.startsWith(placeholderBase)) {
      newUrl = newUrl.slice(placeholderBase.length);
    }

    // If the original URL didn't start with /, but the result does (because URL adds it), remove it.
    // Example: url="path?q=1" -> newUrl="/path" -> we want "path"
    if (!url.startsWith("/") && newUrl.startsWith("/")) {
      newUrl = newUrl.substring(1);
    }
  }

  return { params, newUrl };
}

/**
 * Check if a URL parameter exists.
 */
export function hasUrlParam(paramName: string): boolean {
  if (typeof window === "undefined") return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(paramName);
}

/**
 * Get a single URL parameter value.
 */
export function getUrlParam(paramName: string): string | null {
  if (typeof window === "undefined") return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(paramName);
}

/**
 * Append query parameters to a URL.
 *
 * @param url - The base URL (can be absolute or relative)
 * @param params - Object containing query parameters to append
 * @returns The URL with appended query parameters
 */
export function appendUrlParams(
  url: string,
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  // Check if URL is absolute
  const isAbsolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url);

  const placeholderBase = "http://placeholder";
  const urlInstance = new URL(url, isAbsolute ? undefined : placeholderBase);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      urlInstance.searchParams.set(key, String(value));
    }
  });

  let newUrl = urlInstance.toString();

  if (!isAbsolute) {
    // Remove the placeholder base
    if (newUrl.startsWith(placeholderBase)) {
      newUrl = newUrl.slice(placeholderBase.length);
    }

    // If the original URL didn't start with /, but the result does (because URL adds it), remove it.
    if (!url.startsWith("/") && newUrl.startsWith("/")) {
      newUrl = newUrl.substring(1);
    }
  }

  return newUrl;
}
